/**
 * Firestore Collection Constants
 * 
 * Centralized collection names for consistent usage across the app.
 * All Firestore collection references should use these constants to prevent
 * typos, ensure consistency, and make refactoring easier.
 * 
 * SECURITY: Using constants prevents injection attacks via collection names
 */

export const COLLECTIONS = {
  // Core collections (uppercase for backward compatibility)
  USERS: 'Users',
  PRODUCTS: 'Products',
  ORDERS: 'Orders',
  CART: 'Cart',
  COUPONS: 'Coupons',
  
  // Messaging (lowercase)
  CONVERSATIONS: 'conversations',
  
  // Promotions (lowercase)
  PROMOTIONS: 'promotions',
  
  // Analytics and tracking (lowercase)
  // Raw interaction logs (append-only)
  INTERACTIONS: 'interactions',
  
  // User analytics (one doc per user)
  USER_ANALYTICS: 'user_analytics',
  
  // Aggregated data
  AGGREGATES: 'aggregates',
  
  // Subcollections under aggregates
  PRODUCT_POPULARITY: 'product_popularity',
  CATEGORY_POPULARITY: 'category_popularity', 
  LOCATION_POPULARITY: 'location_popularity',
  
  // Precomputed top lists for fast reads
  PRODUCT_POPULARITY_TOP: 'product_popularity_top',
  
  // Rewards and loyalty (lowercase)
  REWARDS: 'rewards',
  
  // Returns (camelCase matching Firestore rules)
  RETURN_REQUESTS: 'returnRequests',
  RETURNS: 'returns',
  
  // KYC (camelCase matching Firestore rules)
  KYC_REQUESTS: 'kycRequests',
  
  // Banners (lowercase)
  BANNERS: 'banners',
  CATEGORY_BANNERS: 'category_banners',
  SELLER_BANNERS: 'seller_banners',
  
  // Flash sales (lowercase)
  FLASH_SALES: 'flash_sales',
  
  // Seasonal offers (lowercase)
  SEASONAL_OFFERS: 'seasonal_offers',
  
  // Announcements (lowercase)
  ANNOUNCEMENTS: 'announcements',
  
  // Activities (uppercase matching Firestore rules)
  ACTIVITIES: 'Activities',
  
  // Reports (mixed case matching Firestore rules)
  REPORTS: 'Reports',
  FALSE_PRODUCT_REPORTS: 'falseProductReports',
  ERROR_REPORTS: 'errorReports',
  
  // Legacy collection (deprecated - stop writing to this)
  RECOMMENDATIONS: 'recommendations',

  // Affiliate program (lowercase; read-only from web for attribution)
  AFFILIATES: 'affiliates',
} as const;

export type CollectionName = typeof COLLECTIONS[keyof typeof COLLECTIONS];
